import * as React from 'react';
import {
  Radio as ShadcnRadio,
  RadioItem as ShadcnRadioItem,
} from '@/hen-ui/shadcn/radio';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { PhrasingHost } from '@/hen-ui/lib/inline-copy';
import { cn } from '@/hen-ui/utils';

import { RadioItemLabelContext } from './radio-label-context';
import {
  radioCardControlClassName,
  radioCardErrorControlClassName,
  radioCardErrorSelectedControlClassName,
  radioCardSelectedControlClassName,
  radioItemVariants,
} from './radio.variants';

export type RadioAppearance = 'default' | 'card';
export type RadioControlPosition = 'start' | 'end';

export interface RadioOption {
  value: string;
  label: string;
  disabled?: boolean;
  error?: boolean;
}

export interface RadioProps
  extends Omit<React.ComponentPropsWithoutRef<typeof ShadcnRadio>, 'children'> {
  options?: RadioOption[];
  children?: React.ReactNode;
  appearance?: RadioAppearance;
  controlPosition?: RadioControlPosition;
}

export interface RadioItemProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof ShadcnRadioItem>,
    'children'
  > {
  /** Visible choice content. Plain text or phrasing nodes such as avatar and rating. */
  label?: React.ReactNode;
  error?: boolean;
}

interface RadioGroupContextValue {
  appearance: RadioAppearance;
  controlPosition: RadioControlPosition;
  disabled: boolean;
}

const RadioGroupContext = React.createContext<RadioGroupContextValue>({
  appearance: 'default',
  controlPosition: 'start',
  disabled: false,
});

/**
 * One choice in a radio group. The label is the click target and may hold
 * plain text or phrasing content such as an avatar, text, and a rating.
 */
export const RadioItem = React.forwardRef<
  React.ElementRef<typeof ShadcnRadioItem>,
  RadioItemProps
>(
  (
    {
      value,
      disabled = false,
      error = false,
      label,
      className,
      id,
      ...itemProps
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const itemId = id ?? generatedId;
    const group = React.useContext(RadioGroupContext);
    const isDisabled = disabled || group.disabled;
    const control = (
      <ShadcnRadioItem
        {...itemProps}
        ref={ref}
        id={itemId}
        value={value}
        disabled={isDisabled}
        aria-invalid={error || undefined}
        data-hen-id={COMPONENT_IDS.Radio}
        className={cn(
          'disabled:opacity-100',
          group.appearance === 'card' && !error && radioCardControlClassName,
          group.appearance === 'card' &&
            !error &&
            radioCardSelectedControlClassName,
          group.appearance === 'card' &&
            error &&
            radioCardErrorControlClassName,
          group.appearance === 'card' &&
            error &&
            radioCardErrorSelectedControlClassName,
          className,
        )}
      />
    );

    return (
      <label
        htmlFor={itemId}
        data-appearance={group.appearance}
        data-control-position={group.controlPosition}
        className={cn(
          radioItemVariants({
            appearance: group.appearance,
            controlPosition: group.controlPosition,
            error,
          }),
          isDisabled && 'cursor-not-allowed opacity-50',
        )}
      >
        {group.controlPosition === 'start' ? control : null}
        <PhrasingHost>
          <RadioItemLabelContext.Provider value={true}>
            {label}
          </RadioItemLabelContext.Provider>
        </PhrasingHost>
        {group.controlPosition === 'end' ? control : null}
      </label>
    );
  },
);

RadioItem.displayName = 'RadioItem';

/**
 * Radio group following HEN Design System standards.
 *
 * A thin wrapper around the shadcn radio group. Pass `options` for a text
 * label per choice, or `children` (`RadioItem`) when a choice needs richer
 * content. Children replace options when both are set.
 */
export const Radio = React.forwardRef<
  React.ElementRef<typeof ShadcnRadio>,
  RadioProps
>(
  (
    {
      className,
      options,
      children,
      disabled = false,
      appearance = 'default',
      controlPosition = 'start',
      ...props
    },
    ref,
  ) => {
    const hasChildren = React.Children.toArray(children).length > 0;
    const groupContext = React.useMemo<RadioGroupContextValue>(
      () => ({
        appearance,
        controlPosition,
        disabled,
      }),
      [appearance, controlPosition, disabled],
    );

    return (
      <RadioGroupContext.Provider value={groupContext}>
        <ShadcnRadio
          ref={ref}
          disabled={disabled}
          {...props}
          data-hen-id={COMPONENT_IDS.Radio}
          data-appearance={appearance}
          className={cn(appearance === 'card' && 'w-full flex-col', className)}
        >
          {hasChildren
            ? children
            : options?.map((option) => (
                <RadioItem
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  disabled={option.disabled}
                  error={option.error}
                />
              ))}
        </ShadcnRadio>
      </RadioGroupContext.Provider>
    );
  },
);

Radio.displayName = 'Radio';
